import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DEFAULT_TIMEZONE } from "shared/utils/dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

export type RecurrenceType = "none" | "daily" | "weekly";

// No "never" variant on purpose: backend rejects rules without UNTIL/COUNT
// (see schedule_v2.checkIfValidRRule) to prevent runaway .all() expansion.
export type RecurrenceEnd =
  | { type: "until"; date: string }
  | { type: "count"; count: number };

/**
 * Build an RFC 5545 RRULE string for daily/weekly recurrence.
 *
 * - Returns `undefined` for non-recurring.
 * - `RRULE:` prefix is required by the backend parser.
 * - UNTIL is end-of-day in the schedule's timezone, serialised as UTC `Z`
 *   (RFC 5545 §3.3.10 requires UTC UNTIL when DTSTART has a TZID). Without
 *   the end-of-day shift, "ends Mar 31" in WIB cuts off classes after 7am.
 * - BYDAY is local because the backend emits `DTSTART;TZID=...`.
 */
export function buildRRule(
  recurrence: RecurrenceType,
  end: RecurrenceEnd,
  byDay?: string[],
  scheduleTimezone: string = DEFAULT_TIMEZONE,
): string | undefined {
  if (recurrence === "none") return undefined;

  let rule = recurrence === "daily" ? "FREQ=DAILY" : "FREQ=WEEKLY";

  if (recurrence === "weekly" && byDay && byDay.length > 0) {
    rule += `;BYDAY=${byDay.join(",")}`;
  }

  if (end.type === "until") {
    const untilStr =
      dayjs(end.date).tz(scheduleTimezone).endOf("day").utc().format("YYYYMMDDTHHmmss") + "Z";
    rule += `;UNTIL=${untilStr}`;
  } else {
    rule += `;COUNT=${end.count}`;
  }

  return `RRULE:${rule}`;
}
